import { i18n } from '../../i18n/index.js';
// Каскад «компания → контакт → сделка» для форм, которые привязываются к
// сущностям. Раньше он существовал только в форме задачи; форма встречи давала
// три независимых списка, из-за чего можно было выбрать контакт одной компании
// и сделку другой. Поля формы должны называться companyId / contactId / dealId.
import { useMemo } from 'react';
import { Form, type FormInstance } from 'antd';

import { useContacts } from '../contacts/hooks/useContacts.js';
import { useDeals } from '../deals/hooks/useDeals.js';
import { useCompanies } from '../tasks/hooks/useTaskLookups.js';

/** Форма должна содержать эти три поля — на них и работает каскад. */
export interface EntityLinkFormValues {
  companyId?: string | undefined;
  contactId?: string | undefined;
  dealId?: string | undefined;
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface CompanyOption extends SelectOption {
  /**
   * Компания не та, в которой работает выбранный контакт. Список компаний намеренно
   * не фильтруется по контакту — иначе сменить компанию можно было бы только через
   * сброс контакта. Вместо этого помечаем: выбор такой компании контакт очистит.
   */
  foreign: boolean;
}

export interface EntityLinkCascade {
  companyOptions: CompanyOption[];
  contactOptions: SelectOption[];
  dealOptions: SelectOption[];
  companiesLoading: boolean;
  contactsLoading: boolean;
  /** Имя контакта без подписи компании — для показа уже выбранного значения. */
  contactNameById: (id: string) => string;
  /** Подсказка под полем сделки: по чему отфильтрован список. */
  dealHint: string | undefined;
  /** Подсказка под полем компании: меняется, когда выбор компании грозит сбросить контакт. */
  companyHint: string;
  handleCompanyChange: (companyId?: string) => void;
  handleContactChange: (contactId?: string) => void;
  handleDealChange: (dealId?: string) => void;
}

export function useEntityLinkCascade<T extends EntityLinkFormValues>(
  form: FormInstance<T>,
): EntityLinkCascade {
  const selectedCompanyId = Form.useWatch('companyId' as never, form) as string | undefined;
  const selectedContactId = Form.useWatch('contactId' as never, form) as string | undefined;

  const companiesQuery = useCompanies();
  const contactsQuery = useContacts();

  // Сделки приходят уже отфильтрованными: контакт точнее компании, поэтому он в
  // приоритете. Серверный фильтр по компании учитывает и сделки её контактов.
  const { deals } = useDeals(
    selectedContactId
      ? { contactId: selectedContactId }
      : selectedCompanyId
        ? { companyId: selectedCompanyId }
        : {},
  );

  const allContacts = useMemo(() => contactsQuery.data ?? [], [contactsQuery.data]);

  /** Компания выбранного контакта — та, что каскад считает «своей». */
  const contactCompanyId = useMemo(() => {
    if (!selectedContactId) return undefined;
    return allContacts.find((contact) => contact.id === selectedContactId)?.companyId ?? undefined;
  }, [allContacts, selectedContactId]);

  const companyOptions = useMemo(
    () =>
      (companiesQuery.data ?? []).map((company) => ({
        value: company.id,
        label: company.name,
        // Контакта нет или он вообще без компании — помечать нечего.
        foreign: contactCompanyId !== undefined && company.id !== contactCompanyId,
      })),
    [companiesQuery.data, contactCompanyId],
  );

  // Компания выбрана → в списке только её сотрудники; иначе — все контакты.
  const contactOptions = useMemo(() => {
    const source = selectedCompanyId
      ? allContacts.filter((contact) => contact.companyId === selectedCompanyId)
      : allContacts;
    return source.map((contact) => ({
      value: contact.id,
      label: contact.companyRef
        ? `${contact.fullName} · ${contact.companyRef.name}`
        : contact.fullName,
    }));
  }, [allContacts, selectedCompanyId]);

  const dealOptions = useMemo(
    () => deals.map((deal) => ({ value: deal.id, label: deal.title })),
    [deals],
  );

  const contactNameById = (id: string): string =>
    allContacts.find((contact) => contact.id === id)?.fullName ?? id;

  /** Сделка зависит от компании и контакта — при их смене выбранная может стать неуместной. */
  const clearDealSelection = (): void => {
    if (form.getFieldValue('dealId' as never)) {
      form.setFieldValue('dealId' as never, undefined);
    }
  };

  const handleContactChange = (contactId?: string): void => {
    clearDealSelection();
    if (!contactId) return;
    const contact = allContacts.find((item) => item.id === contactId);
    if (contact?.companyId && !form.getFieldValue('companyId' as never)) {
      form.setFieldValue('companyId' as never, contact.companyId);
    }
  };

  /** Выбрали сделку — подтягиваем её контакт и компанию (от сделки или от контакта). */
  const handleDealChange = (dealId?: string): void => {
    if (!dealId) return;
    const deal = deals.find((item) => item.id === dealId);
    if (!deal) return;
    const dealContact = deal.contactId
      ? allContacts.find((item) => item.id === deal.contactId)
      : undefined;
    if (deal.contactId) {
      form.setFieldValue('contactId' as never, deal.contactId);
    }
    const companyId = deal.companyId ?? dealContact?.companyId ?? null;
    if (companyId) {
      form.setFieldValue('companyId' as never, companyId);
    }
  };

  /** Сменили компанию — сбрасываем контакт, если он из другой компании. */
  const handleCompanyChange = (companyId?: string): void => {
    clearDealSelection();
    const contactId = form.getFieldValue('contactId' as never) as string | undefined;
    if (!companyId || !contactId) return;
    const contact = allContacts.find((item) => item.id === contactId);
    if (contact && contact.companyId !== companyId) {
      form.setFieldValue('contactId' as never, undefined);
    }
  };

  return {
    companyOptions,
    contactOptions,
    dealOptions,
    companiesLoading: companiesQuery.isLoading,
    contactsLoading: contactsQuery.isLoading,
    contactNameById,
    companyHint:
      contactCompanyId === undefined
        ? i18n.t(
            'crm.features.features.entity.links.useentitylinkcascade.выбор_компании_оставит_в_списке_контактов_',
          )
        : i18n.t(
            'crm.features.features.entity.links.useentitylinkcascade.компании_помеченные_другая_не_связаны_с_вы',
          ),
    dealHint: selectedContactId
      ? i18n.t(
          'crm.features.features.entity.links.useentitylinkcascade.показаны_сделки_выбранного_контакта',
        )
      : selectedCompanyId
        ? i18n.t(
            'crm.features.features.entity.links.useentitylinkcascade.показаны_сделки_компании_и_ее_контактов',
          )
        : undefined,
    handleCompanyChange,
    handleContactChange,
    handleDealChange,
  };
}
